import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { LuCheck, LuRotateCcw, LuTrash2 } from "react-icons/lu";
import { BASE_URL, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { TabBar } from "../shared/TabBar";
import { CampaignModal } from "./CampaignModal";
import { formatShortDate } from "../shared/dateUtils";

const PORTAL = `${BASE_URL}/portal/api`;

// A campaign is "completed" when it is closed. Draft and open are both live
// work, so they share the Active tab rather than making a third state visible.
const isCompleted = (c) => c.status === "closed";

const cols = "1.9fr 1.3fr 150px 80px 72px";

export function CampaignsPage({ onSelect }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("active");
  const [showNew, setShowNew] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    axios
      .get(`${PORTAL}/campaigns/`)
      .then((r) => {
        setCampaigns(r.data || []);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load campaigns. Check you are still signed in.");
        setLoading(false);
      });
  }, []);

  useEffect(load, [load]);

  const completedCount = useMemo(
    () => campaigns.filter(isCompleted).length,
    [campaigns]
  );
  const shown = useMemo(
    () => campaigns.filter((c) => isCompleted(c) === (tab === "completed")),
    [campaigns, tab]
  );

  const setStatus = async (campaign, status) => {
    setBusyId(campaign.id);
    setError("");
    try {
      await axios.patch(`${PORTAL}/campaigns/${campaign.id}/`, { status });
      load();
    } catch (e) {
      setError("Could not update that campaign.");
    }
    setBusyId(null);
  };

  const remove = async (campaign) => {
    // Spell out the cascade: deleting a campaign takes the tenants' answers
    // with it, and there is no undo.
    if (
      !window.confirm(
        `Delete "${campaign.name}" for ${campaign.site_name}?\n\n` +
          `Its inspection slots, every unit's booking and all access codes will be ` +
          `deleted too. This can't be undone.`
      )
    )
      return;
    setBusyId(campaign.id);
    setError("");
    try {
      await axios.delete(`${PORTAL}/campaigns/${campaign.id}/`);
      load();
    } catch (e) {
      setError("Could not delete that campaign.");
    }
    setBusyId(null);
  };

  const iconButton = {
    background: "none",
    border: "none",
    padding: "4px",
    borderRadius: "4px",
    cursor: "pointer",
    color: colors.slate,
    display: "inline-flex",
    alignItems: "center",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Fire Inspection Bookings</div>
        <button onClick={() => setShowNew(true)} style={styles.addButton}>
          New booking campaign
        </button>
      </div>

      <TabBar
        tabs={["active", "completed"]}
        tabLabels={{
          active: `Active (${campaigns.length - completedCount})`,
          completed: `Completed (${completedCount})`,
        }}
        activeTab={tab}
        onSelect={setTab}
      />

      <div style={styles.content}>
        <div style={styles.card}>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : error ? (
            <div style={{ ...styles.loading, color: colors.red }}>{error}</div>
          ) : shown.length === 0 ? (
            <div style={{ ...styles.loading, lineHeight: "1.6" }}>
              {tab === "completed" ? (
                "No completed booking campaigns yet."
              ) : (
                <>
                  No booking campaigns yet.
                  <div style={{ marginTop: "10px" }}>
                    <button onClick={() => setShowNew(true)} style={styles.addButton}>
                      New booking campaign
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              <div
                style={{ ...styles.tableHeader, gridTemplateColumns: cols, gap: "16px" }}
              >
                <div>Site</div>
                <div>Booking campaign</div>
                <div>Dates</div>
                <div>Booked</div>
                <div />
              </div>

              {shown.map((c) => (
                <div
                  key={c.id}
                  onClick={() => onSelect(c.id)}
                  onMouseEnter={() => setHovered(c.id)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    ...styles.tableRow(hovered === c.id),
                    gridTemplateColumns: cols,
                    gap: "16px",
                    opacity: busyId === c.id ? 0.5 : 1,
                  }}
                >
                  <div style={{ overflow: "hidden" }}>
                    <div style={{ fontWeight: "600", color: colors.text }}>
                      {c.site_name}
                      {c.area_name ? ` · ${c.area_name}` : ""}
                    </div>
                    {c.site_address && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: colors.slate,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.site_address}
                      </div>
                    )}
                  </div>
                  <div style={{ color: colors.text, overflow: "hidden" }}>{c.name}</div>
                  <div style={{ color: colors.slate, whiteSpace: "nowrap" }}>
                    {formatShortDate(c.window_start)} – {formatShortDate(c.window_end)}
                  </div>
                  <div style={{ color: colors.slate, whiteSpace: "nowrap" }}>
                    {c.booked_count}/{c.unit_count}
                  </div>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "flex",
                      gap: "2px",
                      justifyContent: "flex-end",
                      opacity: hovered === c.id ? 1 : 0,
                      transition: "opacity 0.15s",
                    }}
                  >
                    {isCompleted(c) ? (
                      <button
                        onClick={() => setStatus(c, "open")}
                        title="Reopen campaign"
                        style={iconButton}
                      >
                        <LuRotateCcw size={14} />
                      </button>
                    ) : (
                      <button
                        onClick={() => setStatus(c, "closed")}
                        title="Mark complete"
                        style={iconButton}
                      >
                        <LuCheck size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => remove(c)}
                      title="Delete campaign"
                      style={iconButton}
                    >
                      <LuTrash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}

              <div style={styles.count}>
                {shown.length} booking campaign{shown.length !== 1 ? "s" : ""}
              </div>
            </>
          )}
        </div>
      </div>

      {showNew && (
        <CampaignModal
          onClose={() => setShowNew(false)}
          onSaved={(created) => {
            setShowNew(false);
            onSelect(created.id);
          }}
        />
      )}
    </div>
  );
}
